<script setup>
import { computed, inject, provide, ref, shallowRef, toValue, useTemplateRef } from 'vue'
import SimpleSchema from '@/components/simpleSchema.vue'
import Form from '@provise/components/form.vue'
import SelectId from '@/components/select/selectIdObject.vue'
import Step from './step.vue'
import Uploads from './uploads.vue'
import Related from './step_correction/related.vue'
import Transfer from './transfer/correction_link.vue'
import { complaintLabel } from '@/lib/transformer'
import { useRouter } from 'vue-router'
import stepsConfig from './steps'

const router = useRouter()
const model = defineModel({})
defineProps({
  edit: Boolean,
  editPermission: {
    type: Boolean,
    default: true,
  },
})
defineEmits(['save'])

const stepsNames = Object.keys(stepsConfig)

const categories = inject('categories', ref([]))
const apiResource = inject('apiResource')

const schema = computed(() => {
  const tp = model.value.request_category?.id
  const ct = categories.value?.find(c => c.id == tp)

  if (!ct || !ct.display) {
    return []
  }

  return JSON.parse(ct.display)
})

const isEditing = shallowRef(false)

const form = useTemplateRef('complaint-correction-form')
const isch = ref({
  request_category: [null, mv => mv?.request_category?.name, 'Категория обращения'],
  archive: [null, mv => mv?.archive?.value],
  description: [null, mv => [mv.description]],
  event_type: [null, mv => mv?.event_type?.value],
  med_received: [null, mv => (mv.med_received ? 'Да' : 'Нет')],
  fn_received: [null, mv => (mv.fn_received ? 'Да' : 'Нет')],
  fn_criticality_event: [null, mv => mv.fn_criticality_event?.value],
  detail_description: [null, mv => [mv.detail_description]],
  provide_results: [null, mv => (mv.provide_results ? 'Да' : 'Нет')],
  product_units: [null, mv => mv?.product_units?.value],
  doubt_reason: [null, mv => [mv.doubt_reason]],
  pharm_contact: [null, mv => [mv.pharm_contact]],
  defect_category: [null, mv => mv?.defect_category?.value],
  defect_type: [null, mv => mv?.defect_type?.value],
  provider_name: [null, mv => mv?.provider_name?.value],
  pharm_method: [null, mv => mv?.pharm_method?.value],
  used_as_lp: [null, mv => (mv?.used_as_lp ? 'Да' : 'Нет')],
  sample_on_stock: [null, mv => (mv?.sample_on_stock ? 'Да' : 'Нет')],
  sample_is_available: [null, mv => (mv?.sample_is_available ? 'Да' : 'Нет')],
  description_photo: [null, mv => (mv?.description_photo ? 'Да' : 'Нет')],
  applicable_swedish: [null, mv => (mv?.applicable_swedish ? 'Да' : 'Нет')],
  manufacturer_lp: [null, mv => mv?.manufacturer_lp?.value],
  product_suspect: [
    null,
    mv => commons.medicineLabel(mv?.product_suspect),
    // "претензия в отношении подлинности"
    mv =>
      mv?.request_category?.sysname == 'genuine_complaint'
        ? 'Подозреваемый лекарственный препарат'
        : undefined,
  ],
  registration_country: [null, mv => mv?.registration_country?.value],
  place_country: [null, mv => mv?.place_country?.value],
  pharm_country: [null, mv => mv?.pharm_country?.value],
  subcomplaints: [null, ischRelated, 'Связанные обращения'],
})
const schemaKeyPrefix = computed(() => {
  const rc = model.value.request_category?.id
  return categories.value.find(({ id }) => rc == id)?.sysname
})
const categoryEnabled = computed(() => {
  const mv = toValue(model)
  const currStatusIdx = stepsNames.indexOf(mv.status?.key)
  const corrStatusIdx = stepsNames.indexOf('correction')
  return currStatusIdx <= corrStatusIdx
})

function ischRelated(mv) {
  if (!mv?.subcomplaints?.length) {
    return
  }
  return [
    '<div class="vstack gap-2">',
    ...mv.subcomplaints.map(sc => {
      const pth = router.resolve({ name: 'complaint.item', params: { id: sc.id } })
      let label = `${complaintLabel(sc)} (#${sc.id})`
      return `<a class="d-block" href="${pth.href}" target="_blank">${label}</a>`
    }),
    '</div>',
  ]
}

provide('ss:key-prefix', schemaKeyPrefix)
</script>
<template>
  <Step :edit="edit" v-model="model" :sch="schema" :isch="isch" name="correction">
    <Form
      :entity="model"
      entity-name="complaints"
      :entity-rbac="['common']"
      :edit-permission="editPermission"
      :edit-label="model.sla_users?.correction ? null : 'Взять в работу'"
      @form:apply="$emit('save', () => form.reset())"
      @form:editable="isEditing = $event"
      @form:reset="apiResource('get')"
      sticky-controls
      ref="complaint-correction-form"
    >
      <div class="mb-3">
        <label class="form-label" for="request-refinement-type">Выберите категорию обращения</label>
        <SelectId
          :options="categories"
          v-model="model.request_category"
          :disabled="!categoryEnabled"
          values="label"
        />
      </div>

      <Related :self="model.id" v-model="model.subcomplaints" />

      <div class="form-schema" v-if="model.request_category?.id">
        <SimpleSchema
          :simplsch="schema"
          @update:model-value="model = $event"
          :model-value="model"
        />

        <Uploads v-model="model" class="g-full" division="complaint.correction" />
      </div>
      <slot name="sla" />
      <Transfer />
      <template #post-controls>
        <div class="text-muted" v-if="!model.request_category?.id">
          Выберите категорию для перехода на следующий этап
        </div>
        <slot name="form-post-controls" v-if="!isEditing" />
      </template>
    </Form>
  </Step>
</template>
